import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, useNavigate, Link, NavigateFunction } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Eye, EyeOff, Lock, Check, User } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { RoleRoutes, UserRole } from '@/types/global.types';

type Status = 'loading' | 'verified' | 'error' | 'setting_password' | 'success' | 'logging_in';

// Helper function to get the correct profile path based on user role and tenant
const getProfilePath = (role: UserRole, tenantId: string | null): string => {
  if (role === UserRole.SUPER_ADMIN) {
    return '/admin/profile';
  }
  if (!tenantId) {
    return '/login';
  }
  const roleRoute = RoleRoutes[role];
  return roleRoute.replace(':tenantId', tenantId) + '/profile';
};

// Success component with auto-redirect to profile
const SuccessWithRedirect: React.FC<{ navigate: NavigateFunction; isOnboarding: boolean; profilePath: string }> = ({ navigate, isOnboarding, profilePath }) => {
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to profile with onboarding flag if new user created by staff
          navigate(isOnboarding ? `${profilePath}?onboarding=true` : profilePath);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, isOnboarding, profilePath]);

  return (
    <div className="text-center py-8">
      <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} className="text-green-600" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        Compte activé avec succès!
      </h2>
      <p className="text-gray-500 mb-4">
        Vous êtes maintenant connecté. Vous allez être redirigé vers votre profil.
      </p>
      {isOnboarding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-blue-700">
            <User size={20} />
            <span className="font-medium">Complétez votre profil</span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            Veuillez compléter vos informations et soumettre votre profil pour validation.
          </p>
        </div>
      )}
      <p className="text-sm text-gray-400 mb-6">
        Redirection automatique dans {countdown} seconde{countdown > 1 ? 's' : ''}...
      </p>
      <button
        onClick={() => navigate(isOnboarding ? `${profilePath}?onboarding=true` : profilePath)}
        className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium w-full"
      >
        Accéder à mon profil
      </button>
    </div>
  );
};

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token: tokenFromPath } = useParams<{ token?: string }>();
  const token = tokenFromPath || searchParams.get('token');
  const { setAuth } = useAuthStore();

  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [passwordSetupToken, setPasswordSetupToken] = useState('');
  const [isOnboarding, setIsOnboarding] = useState(false); // True if user was created by staff
  const [profilePath, setProfilePath] = useState('/login'); // Will be set after successful login

  // Password form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const passwordRequirements = [
    { label: 'Au moins 8 caractères', valid: password.length >= 8 },
    { label: 'Au moins une majuscule', valid: /[A-Z]/.test(password) },
    { label: 'Au moins une minuscule', valid: /[a-z]/.test(password) },
    { label: 'Au moins un chiffre', valid: /[0-9]/.test(password) },
  ];

  const isPasswordValid = passwordRequirements.every((req) => req.valid);
  const doPasswordsMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Token de vérification manquant');
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/verify-email?token=${token}`
        );
        const data = await response.json();

        if (response.ok) {
          setEmail(data.email);
          setPasswordSetupToken(data.passwordSetupToken);
          setStatus('verified');
        } else {
          setStatus('error');
          setMessage(data.message || 'Erreur lors de la vérification');
        }
      } catch {
        setStatus('error');
        setMessage('Erreur de connexion au serveur');
      }
    };

    verifyEmail();
  }, [token]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setErrorMessage('Le mot de passe ne respecte pas les exigences');
      return;
    }

    if (!doPasswordsMatch) {
      setErrorMessage('Les mots de passe ne correspondent pas');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setStatus('setting_password');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: passwordSetupToken, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if user was created by staff (onboarding flow)
        setIsOnboarding(data.isCreatedByStaff || false);

        // Auto-login the user
        setStatus('logging_in');
        try {
          const authResponse = await authService.login({ email, password });
          setAuth(authResponse.user, authResponse.tokens);
          // Calculate the correct profile path based on user role and tenant
          const userProfilePath = getProfilePath(authResponse.user.role as UserRole, authResponse.user.tenantId);
          setProfilePath(userProfilePath);
          setStatus('success');
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          // Fall back to login page if auto-login failed
          setProfilePath('/login');
          setStatus('success');
        }
      } else {
        setStatus('verified');
        setErrorMessage(data.message || 'Erreur lors de la configuration');
      }
    } catch {
      setStatus('verified');
      setErrorMessage('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="h-16 w-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl font-bold text-white">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">NEXEDU</h1>
        </div>

        {/* Loading State */}
        {status === 'loading' && (
          <div className="text-center py-8">
            <Loader2 size={48} className="mx-auto text-primary-600 animate-spin mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Vérification en cours...
            </h2>
            <p className="text-gray-500">Veuillez patienter</p>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center py-8">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Erreur de vérification
            </h2>
            <p className="text-gray-500 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Retour à la connexion
            </Link>
          </div>
        )}

        {/* Verified - Show Password Form */}
        {status === 'verified' && (
          <>
            <div className="text-center mb-6">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Email vérifié!
              </h2>
              <p className="text-gray-500 mt-2">
                Configurez maintenant votre mot de passe pour <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errorMessage}
                </div>
              )}

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Entrez votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Exigences du mot de passe:
                </p>
                <ul className="space-y-2">
                  {passwordRequirements.map((req, index) => (
                    <li
                      key={index}
                      className={`flex items-center gap-2 text-sm ${
                        req.valid ? 'text-green-600' : 'text-gray-500'
                      }`}
                    >
                      {req.valid ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <div className="w-4 h-4 border border-gray-300 rounded-full" />
                      )}
                      {req.label}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      confirmPassword && !doPasswordsMatch
                        ? 'border-red-300'
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirmez votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {confirmPassword && !doPasswordsMatch && (
                  <p className="mt-2 text-sm text-red-600">
                    Les mots de passe ne correspondent pas
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !isPasswordValid || !doPasswordsMatch}
                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Configuration en cours...
                  </>
                ) : (
                  'Activer mon compte'
                )}
              </button>
            </form>
          </>
        )}

        {/* Setting Password State */}
        {status === 'setting_password' && (
          <div className="text-center py-8">
            <Loader2 size={48} className="mx-auto text-primary-600 animate-spin mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Configuration du mot de passe...
            </h2>
            <p className="text-gray-500">Veuillez patienter</p>
          </div>
        )}

        {/* Logging In State */}
        {status === 'logging_in' && (
          <div className="text-center py-8">
            <Loader2 size={48} className="mx-auto text-primary-600 animate-spin mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Connexion en cours...
            </h2>
            <p className="text-gray-500">Vous allez être redirigé vers votre profil</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <SuccessWithRedirect navigate={navigate} isOnboarding={isOnboarding} profilePath={profilePath} />
        )}
      </div>
    </div>
  );
};
